import { DefaultLayout, Layout } from "@facade/shared";
import { IndeterminateProgress, SpinnerFallback } from "@facade/components/ui";
import {
  ProjectsHomePageSkeleton,
  ViewProjectLayout,
} from "@facade/features/projects";
import { Suspense, lazy } from "react";
import { IfcViewerSplash } from "@facade/features/visualizer/pages/IfcViewerPage/components/IfcViewerSplash";

import { ProtectedRoute } from "@facade/auth";
import { RouteErrorPage } from "./RouteErrorPage";
import { createBrowserRouter } from "react-router-dom";

// Auth pages
const SignupPage = lazy(() => import("@facade/features/auth/pages/SignupPage"));
const SigninPage = lazy(() => import("@facade/features/auth/pages/SigninPage"));
const ConfirmSignupPage = lazy(
  () => import("@facade/features/auth/pages/ConfirmSignupPage"),
);

// Projects
const ProjectsHomePage = lazy(
  () => import("@facade/features/projects/pages/ProjectsHomePage"),
);
const ViewProjectPage = lazy(
  () => import("@facade/features/projects/pages/ViewProjectPage"),
);
const IfcViewerPage = lazy(
  () => import("@facade/features/visualizer/pages/IfcViewerPage"),
);

// Account
const AccountPage = lazy(
  () => import("@facade/features/account/pages/AccountPage"),
);

// Organization
const ViewOrganizationPage = lazy(
  () => import("@facade/features/organization/pages/ViewOrganizationPage"),
);

function withSuspense(element: React.ReactNode, fallback?: React.ReactNode) {
  return (
    <Suspense fallback={fallback ?? <SpinnerFallback />}>{element}</Suspense>
  );
}

export const router = createBrowserRouter([
  {
    path: "/projects/:projectId/models/:modelId/viewer",
    errorElement: <RouteErrorPage />,
    element: withSuspense(
      <ProtectedRoute>
        <IfcViewerPage />
      </ProtectedRoute>,
      <IfcViewerSplash />,
    ),
  },
  {
    path: "/projects/:projectId/federations/:federationId/viewer",
    errorElement: <RouteErrorPage />,
    element: withSuspense(
      <ProtectedRoute>
        <IfcViewerPage />
      </ProtectedRoute>,
      <IfcViewerSplash />,
    ),
  },
  {
    path: "/",
    errorElement: <RouteErrorPage />,
    element: withSuspense(
      <ProtectedRoute>
        <Layout />
      </ProtectedRoute>,
    ),
    children: [
      {
        index: true,
        element: (
          <DefaultLayout>
            {withSuspense(<ProjectsHomePage />, <ProjectsHomePageSkeleton />)}
          </DefaultLayout>
        ),
      },
      {
        path: "account/:tab?",
        element: <DefaultLayout>{withSuspense(<AccountPage />)}</DefaultLayout>,
      },
      {
        path: "organization/:organizationId/:tab?",
        element: (
          <DefaultLayout>
            {withSuspense(<ViewOrganizationPage />)}
          </DefaultLayout>
        ),
      },
      {
        path: "projects/:projectId/:tab?/:resourceId?/:action?",
        element: (
          <ViewProjectLayout>
            {withSuspense(<ViewProjectPage />, <IndeterminateProgress />)}
          </ViewProjectLayout>
        ),
      },
    ],
  },
  {
    path: "/signin",
    errorElement: <RouteErrorPage />,
    element: withSuspense(<SigninPage />),
  },
  {
    path: "/signup",
    errorElement: <RouteErrorPage />,
    element: withSuspense(<SignupPage />),
  },
  {
    path: "/confirm-signup",
    errorElement: <RouteErrorPage />,
    element: withSuspense(<ConfirmSignupPage />),
  },
]);
